// DraggableModal.js

import React, { useRef, useState, useEffect } from 'react';
import { IconButton, Portal, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Iframe from 'react-iframe';
import './DraggableModal.css';
import { useTranslation } from 'react-i18next';
import { Rnd } from 'react-rnd';

const isIosLikeDevice = () => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    if (/iPad|iPhone|iPod/.test(ua)) return true;
    // iPadOS 13+ reports as MacIntel with touch
    return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
};

const DraggableModal = ({
    bigIframeModalOpen,
    handleClose,
    openedIframeUrl,
    handleCloseIframeModal,
}) => {
    const nodeRef = useRef(null);
    const [showPdfIframe, setShowPdfIframe] = useState(true);
    const [viewerUrl, setViewerUrl] = useState('');
    const { i18n, t } = useTranslation();
    const direction = i18n.dir();
    const container = document.getElementById('root');
    const iosLike = isIosLikeDevice();

    const [modalSize, setModalSize] = useState({
        width: Math.min(600, typeof window !== 'undefined' ? window.innerWidth - 24 : 600),
        height: typeof window !== 'undefined' ? window.innerHeight * 0.8 : 500,
    });
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });

    // Calculate center position when modal opens
    useEffect(() => {
        if (bigIframeModalOpen) {
            const modalWidth = modalSize.width;
            const modalHeight = modalSize.height;
            const x = (window.innerWidth - modalWidth) / 2;
            const y = Math.max(8, (window.innerHeight - modalHeight) / 2);
            setModalPosition({ x, y });
            setShowPdfIframe(true);
        }
    }, [bigIframeModalOpen, modalSize.width, modalSize.height]);

    // iOS Safari often blanks PDF iframes for remote URLs — blob URLs render more reliably.
    useEffect(() => {
        let revoked = false;
        let objectUrl = '';

        const load = async () => {
            if (!bigIframeModalOpen || !openedIframeUrl) {
                setViewerUrl('');
                return;
            }

            if (!iosLike) {
                setViewerUrl(openedIframeUrl);
                return;
            }

            try {
                const res = await fetch(openedIframeUrl);
                if (!res.ok) throw new Error('fetch failed');
                const blob = await res.blob();
                if (revoked) return;
                const pdfBlob =
                    blob.type && blob.type.includes('pdf')
                        ? blob
                        : new Blob([blob], { type: 'application/pdf' });
                objectUrl = URL.createObjectURL(pdfBlob);
                setViewerUrl(objectUrl);
            } catch {
                if (!revoked) setViewerUrl(openedIframeUrl);
            }
        };

        load();

        return () => {
            revoked = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [bigIframeModalOpen, openedIframeUrl, iosLike]);

    const openExternally = (e) => {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        if (openedIframeUrl) window.open(openedIframeUrl, '_blank', 'noopener,noreferrer');
    };

    return (
        bigIframeModalOpen && (
            <Portal container={container}>
                <Rnd
                    bounds="window"
                    size={{ width: modalSize.width, height: modalSize.height }}
                    position={{ x: modalPosition.x, y: modalPosition.y }}
                    minWidth={280}
                    minHeight={200}
                    enableResizing={{
                        top: true,
                        right: true,
                        bottom: true,
                        left: true,
                        topRight: true,
                        bottomRight: true,
                        bottomLeft: true,
                        topLeft: true,
                    }}
                    onDragStop={(e, d) => {
                        setModalPosition({ x: d.x, y: d.y });
                    }}
                    onResizeStop={(e, direction, ref, delta, position) => {
                        setModalSize({
                            width: ref.offsetWidth,
                            height: ref.offsetHeight,
                        });
                        setModalPosition(position);
                    }}
                    dragHandleClassName="handle"
                    cancel=".draggable-modal-no-drag, .MuiIconButton-root, button, a"
                    style={{ zIndex: 2000 }}
                >
                    <div ref={nodeRef} className="draggable-modal">
                        <div className={`modal-header ${direction}`}>
                            <div className="documentPreview-modal-title handle">
                                {t('DocumentPreview')}
                            </div>

                            <div className="draggable-modal-header-actions draggable-modal-no-drag">
                                {openedIframeUrl && (
                                    <Button
                                        size="small"
                                        variant="text"
                                        startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                                        onClick={openExternally}
                                        onPointerDown={(e) => e.stopPropagation()}
                                        onTouchStart={(e) => e.stopPropagation()}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        sx={{ textTransform: 'none', minWidth: 0 }}
                                    >
                                        {t('OpenDocument') || 'Open PDF'}
                                    </Button>
                                )}
                                <IconButton
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleCloseIframeModal();
                                    }}
                                    onPointerDown={(e) => e.stopPropagation()}
                                    onTouchStart={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    aria-label={t('Exit') || 'Close'}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </div>
                        </div>
                        <div
                            className="documentPreview-modal-content"
                            style={{ height: 'calc(100% - 50px)', overflow: 'hidden' }}
                        >
                            {showPdfIframe && viewerUrl && (
                                iosLike ? (
                                    <object
                                        data={viewerUrl}
                                        type="application/pdf"
                                        className="draggable-modal-pdf"
                                        aria-label="PDF Viewer"
                                    >
                                        <embed
                                            src={viewerUrl}
                                            type="application/pdf"
                                            className="draggable-modal-pdf"
                                        />
                                        <div className="draggable-modal-pdf-fallback">
                                            <Button
                                                variant="contained"
                                                startIcon={<OpenInNewIcon />}
                                                onClick={openExternally}
                                            >
                                                {t('OpenDocument') || 'Open PDF'}
                                            </Button>
                                        </div>
                                    </object>
                                ) : (
                                    <Iframe
                                        url={`${viewerUrl}#page=1&view=FitH,FitV`}
                                        width="100%"
                                        height="100%"
                                        style={{ border: 'none', flexGrow: 1 }}
                                        title="PDF Viewer"
                                    />
                                )
                            )}
                        </div>
                    </div>
                </Rnd>
            </Portal>
        )
    );
};

export default DraggableModal;
